import {useMemo} from 'react';
import type {
  SelectOption,
  TaskDefinitionRequest,
} from 'src/api/ProactApiClient';
import {FormStateEnum, TaskDefinitionStateEnum} from 'src/api/ProactApiClient';
import {DueDatePreviewForm} from 'src/app/forms/DueDatePreviewForm';
import {taskFieldDefinitions} from 'src/app/tasks/taskHelpers';
import type {UseTaskFeatureFormMeta} from 'src/app/tasks/createTaskFeatureEditor';
import {
  createTaskFeatureEditor,
  useComplianceStandardOptions,
} from 'src/app/tasks/createTaskFeatureEditor';
import {mapComplianceStandardIds} from 'src/app/tasks/taskFeatureTypes';
import {useGetFormDefinitionSelectOptionsQuery} from 'src/redux/queries/formDefinition';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSkipTokensByFeature from 'src/shared/hooks/useSkipTokensByFeature';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';

/** Form-task transition target: the linked submission when one exists, else a new task-bound form. */
export const getAssignedFormTransitionUrl = (
  formDefinitionId: number | string,
  assignmentId: number | string,
  formSubmissionId: number | null | undefined,
  formSubmissionState: FormStateEnum | null | undefined
): string => {
  if (!formSubmissionId) {
    return PathUtility.FormTaskEdit(formDefinitionId, assignmentId);
  }
  return formSubmissionState === FormStateEnum.Draft
    ? PathUtility.FormEdit(formDefinitionId, formSubmissionId)
    : PathUtility.FormSubmission(formDefinitionId, formSubmissionId, 'content');
};

export const getAssignedFormMeta = (
  taskState: TaskDefinitionStateEnum,
  standards: SelectOption[],
  forms: SelectOption[]
): IFormFieldV2[] => [
  taskFieldDefinitions.projectId,
  taskFieldDefinitions.willAssignTo,
  taskFieldDefinitions.formDefinitionId(forms),
  taskFieldDefinitions.approvalFlowType,
  taskFieldDefinitions.dueInDays,
  taskState === TaskDefinitionStateEnum.Draft
    ? taskFieldDefinitions.previewDueDate
    : taskFieldDefinitions.dueDate,
  taskFieldDefinitions.completionType,
  taskFieldDefinitions.autoAssignment,
  taskFieldDefinitions.assetId,
  taskFieldDefinitions.complianceStandards(standards),
];

export const mapAssignedFormRequest = (
  values: IFormDataV2
): TaskDefinitionRequest => ({
  projectId: values.projectId,
  targetSourceEntityRequest: values.sourceEntities,
  formDefinitionId: values.formDefinitionId,
  approvalFlowType: values.approvalFlowType,
  dueInDays: values.dueInDays,
  completionType: values.completionType,
  refresh: values.refresh,
  assetId: values.assetId,
  targetComplianceStandardIds: mapComplianceStandardIds(values),
  subject: values.subject ?? '',
});

export const useAssignedFormMeta: UseTaskFeatureFormMeta = (
  taskState,
  transformMeta
) => {
  const {skipForms} = useSkipTokensByFeature();
  const forms = useGetFormDefinitionSelectOptionsQuery(
    {start: 0, length: 10000, active: true, includeSubforms: true},
    {skip: skipForms}
  );
  const {
    standards,
    canViewCompliance,
    isLoading: isStandardsLoading,
    error: standardsError,
  } = useComplianceStandardOptions();
  const isLoading =
    forms.isFetching ||
    forms.isLoading ||
    isStandardsLoading;
  const error = forms.error ?? standardsError;
  const formMeta = useMemo(() => {
    const fields = getAssignedFormMeta(
      taskState,
      standards,
      forms.data?.data ?? []
    ).filter(
      field =>
        canViewCompliance || field.name !== 'complianceStandardIds'
    );
    return transformMeta ? transformMeta(fields) : fields;
  }, [canViewCompliance, forms.data?.data, standards, taskState, transformMeta]);
  return {formMeta, isLoading, error};
};

export const AssignedFormEditor = createTaskFeatureEditor(
  useAssignedFormMeta,
  mapAssignedFormRequest,
  DueDatePreviewForm
);
